import { motion } from "framer-motion";
import { useSettings } from "@/context/SettingsContext";
import { useCollectionsQuery } from "@/lib/queries";
import {
  HOME_CATEGORIES,
  selectCategoryAndScroll,
  resolveCategoryCardImage,
} from "@/lib/categories";
import CategoryCard from "@/components/CategoryCard";

// Homepage "Shop by Category" section — sits between Hero and Collections.
// Renders the 6 non-"All" category cards. Clicking a card scrolls the user
// down to Collections and preselects that category (see selectCategoryAndScroll).
export default function ShopByCategory() {
  const { settings } = useSettings();
  const { data: products = [] } = useCollectionsQuery();
  const cardImages = settings?.category_card_images || {};

  return (
    <section
      id="shop-by-category"
      className="relative py-20 md:py-28 bg-[#0A0A0A] border-t border-[#141414]"
      data-testid="shop-by-category-section"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="text-center mb-12 md:mb-16">
          <div
            className="text-[0.7rem] tracking-[0.32em] uppercase text-[#D4AF37] font-semibold mb-4"
            data-testid="shop-by-category-eyebrow"
          >
            Curated by category
          </div>
          <h2
            className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white max-w-2xl mx-auto"
            data-testid="shop-by-category-headline"
          >
            Shop by Category
          </h2>
          <p className="text-white/60 mt-4 max-w-xl mx-auto text-sm md:text-base">
            Choose your world — every piece hand-selected and quality-checked before it leaves us.
          </p>
        </div>

        <div
          className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6"
          data-testid="shop-by-category-grid"
        >
          {HOME_CATEGORIES.map((cat, i) => {
            const image = resolveCategoryCardImage(cat.value, cardImages, products);
            const label = cat.homeLabel || cat.label;
            return (
              <motion.div
                key={cat.value}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.55, delay: i * 0.06 }}
              >
                <CategoryCard
                  label={label}
                  image={image}
                  onClick={() => selectCategoryAndScroll(cat.value)}
                  testId={`shop-category-card-${cat.slug}`}
                  size="md"
                  aspect="4/5"
                />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
